import { acquireTokenSilent } from '../config/msalConfig';

export interface SuburbLocation {
  linz_id: number;
  name: string;
  type: string;
  major_name?: string | null;
  territorial_authority: string;
  lat: number;
  lng: number;
  island?: string | null;
  is_legacy: boolean;
}

export interface LocationSkillConfig {
  status_values: string[];
  source_values: string[];
  confidence_values: string[];
  routing_thresholds: {
    distance_high_km: number;
    distance_gap_high_km: number;
    distance_medium_km: number;
    distance_gap_medium_km: number;
    confidence_suppression: number;
  };
}

export class SuburbLocationService {
  private static baseUrl =
    (import.meta.env.VITE_FUNCTION_APP_URL || 'https://func-axis-referral-dev.azurewebsites.net') + '/api';

  private static async getToken(): Promise<string> {
    const tokenResult = await acquireTokenSilent();
    return tokenResult.accessToken;
  }

  static async searchSuburbs(
    query: string,
    limit = 50,
    offset = 0,
  ): Promise<{ suburbs: SuburbLocation[]; total: number }> {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (query.trim()) params.set('q', query.trim());
    const response = await fetch(`${this.baseUrl}/rules/suburb-locations?${params}`, {
      headers: { Authorization: `Bearer ${await this.getToken()}` },
    });
    if (!response.ok) throw new Error('Failed to load suburb locations');
    const data = await response.json();
    return { suburbs: data.suburbs || [], total: data.total || 0 };
  }

  static async updateSuburb(
    name: string,
    territorialAuthority: string,
    updates: Partial<Pick<SuburbLocation, 'lat' | 'lng' | 'is_legacy' | 'major_name'>>,
  ): Promise<void> {
    const response = await fetch(
      `${this.baseUrl}/rules/suburb-locations/${encodeURIComponent(name)}/${encodeURIComponent(territorialAuthority)}`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      },
    );
    if (!response.ok) throw new Error('Failed to update suburb');
  }

  static async getLocationConfig(): Promise<LocationSkillConfig> {
    const response = await fetch(`${this.baseUrl}/rules/location-config`, {
      headers: { Authorization: `Bearer ${await this.getToken()}` },
    });
    if (!response.ok) throw new Error('Failed to load location config');
    return response.json();
  }
}
